import { useCallback, useEffect, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiRequestError } from './api';
import { useT, type Clave } from './i18n';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Icon, LanguageToggle, Loading, ThemeToggle, type IconName } from './components/ui';
import { CreatorDetail } from './pages/CreatorDetail';
import { Credentials } from './pages/Credentials';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Manual } from './pages/Manual';
import { NewCreator } from './pages/NewCreator';
import { Laboratorio } from './pages/Laboratorio';
import { Platforms } from './pages/Platforms';
import { Bienvenida } from './pages/Bienvenida';
import { Referencias } from './pages/Referencias';
import { Usuarios } from './pages/Usuarios';
import { Services } from './pages/Services';
import { Settings } from './pages/Settings';
import { Modelos } from './pages/Modelos';
import { Paginas } from './pages/Paginas';
import { WorkflowRun } from './pages/WorkflowRun';
import { Estadisticas } from './pages/Estadisticas';
import { EstadisticasGenerales } from './pages/EstadisticasGenerales';
import { Herramientas } from './pages/Herramientas';

/**
 * El menú.
 *
 * Guarda la **clave** de cada etiqueta y no la etiqueta: si guardara el texto habría que
 * reconstruir el arreglo en cada cambio de idioma, y el que quedó escrito al montar sería el que
 * se sigue viendo. La traducción ocurre al pintar.
 */
/** Un destino del menú. `corta` sólo la usa la barra del teléfono, donde el ancho es de 78 px. */
type Destino = { to: string; clave: Clave; corta?: Clave; icon: IconName };

const NAV: Destino[] = [
  { to: '/', clave: 'nav.inicio', icon: 'home' },
  { to: '/nueva', clave: 'nav.nueva', corta: 'nav.nuevaCorta', icon: 'plus' },
  { to: '/estadisticas', clave: 'nav.estadisticas', icon: 'gauge' },
  { to: '/herramientas', clave: 'nav.herramientas', icon: 'star' },
  { to: '/consumo', clave: 'nav.consumo', icon: 'gauge' },
  { to: '/modelos', clave: 'nav.modelos', icon: 'spark' },
  { to: '/servicios', clave: 'nav.servicios', icon: 'plug' },
  // Credenciales las ve cualquiera: son de quien las carga, no del sistema. Ver el comentario
  // en `routes.ts`, donde se quitó el gateo por rol que rompía el alta de una cuenta nueva.
  { to: '/credenciales', clave: 'nav.credenciales', icon: 'key' },
];

/**
 * El menú, con el laboratorio al final y sólo si está disponible.
 *
 * Va último a propósito: no es parte del trabajo de todos los días y cada botón llama a un
 * proveedor pago. Entre «Consumo» y «Credenciales» invitaría a pulsarlo.
 *
 * Decía acá que gastaba «sin pasar por los topes», y hacía años que no era cierto: el portón de
 * `/api/lab/*` somete cada sonda al modo USD 0. Lo que no descuenta es el tope diario de
 * neuronas, porque esos proveedores no facturan en neuronas. La frase vieja llegó a copiarse al
 * manual como si fuera un aviso.
 */
function navFor(labEnabled: boolean, esAdmin: boolean): Destino[] {
  const base = [
    ...NAV,
    { to: '/bienvenida', clave: 'nav.guia' as Clave, icon: 'book' as IconName },
  ];
  // Las cuentas sólo las administra quien puede crearlas: al resto no le sirve de nada verla.
  const conUsuarios = esAdmin
    ? [...base, { to: '/usuarios', clave: 'nav.cuentas' as Clave, icon: 'users' as IconName }]
    : base;

  return labEnabled
    ? [...conUsuarios, { to: '/laboratorio', clave: 'nav.laboratorio' as Clave, icon: 'flask' }]
    : conUsuarios;
}

export function App() {
  const { t } = useT();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean | null>(null);
  /**
   * Si el laboratorio está disponible.
   *
   * Lo decide el servidor, que es quien apaga `/api/lab/*` fuera de desarrollo. Decidirlo acá
   * daría una pestaña cuyas rutas responden 404 — o peor, escondería una que sí funciona.
   */
  const [labEnabled, setLabEnabled] = useState(false);
  const [esAdmin, setEsAdmin] = useState(false);
  /**
   * Si todavía no terminó la guía de arranque.
   *
   * Una cuenta nueva abre el panel y no ve nada —sus influencers son suyas y no tiene ninguna—,
   * así que el tablero vacío se lee como roto. Se la manda a la guía hasta que diga que la leyó.
   */
  const [necesitaGuia, setNecesitaGuia] = useState(false);
  /** Si la hoja de «Más» está abierta. Sólo existe en el teléfono. */
  const [masAbierto, setMasAbierto] = useState(false);

  /**
   * Quién sos, releído en cada cambio de sesión y no sólo al arrancar.
   *
   * ── El error que arregla ──────────────────────────────────────────────────────
   * Antes el login sólo hacía `setAuthed(true)`: entraba, pero no volvía a preguntar quién
   * había entrado. Las banderas de rol quedaban las del anterior, en memoria.
   *
   * Se vio entero: alguien salió de una sesión de administrador y entró como operador en la
   * misma pestaña. El menú siguió mostrando «Cuentas» y «Laboratorio», y la URL siguió siendo
   * la que estaba abierta —`/usuarios`—, que se leyó como que lo había llevado ahí. El servidor
   * respondía 401 correctamente; lo que mentía era la pantalla.
   *
   * Un cambio de sesión tiene que releer la sesión. Parece obvio dicho así, y el `setAuthed`
   * suelto lo escondía.
   */
  const leerSesion = useCallback(
    (viene: 'arranque' | 'login' = 'arranque') => {
      api
        .get<{
          labEnabled?: boolean;
          user?: { role?: string; onboardedAt?: string | null };
        }>('/api/auth/me')
        .then((me) => {
          const guia = me.user?.onboardedAt == null && me.user?.role !== 'admin';

          setLabEnabled(me.labEnabled === true);
          setEsAdmin(me.user?.role === 'admin');
          setNecesitaGuia(guia);
          setAuthed(true);

          /**
           * Entrar lleva a algún lado, y no a donde estaba abierto.
           *
           * El login no tocaba la URL, así que quien entraba caía en la última página de la
           * sesión anterior — alguien salió de una de administrador y entró como operador, y
           * quedó en `/usuarios`, una pantalla que ya no era suya. Se lee como que el sistema
           * lo mandó ahí.
           *
           * Sólo al iniciar sesión: al arrancar con sesión abierta, la URL escrita o marcada
           * es exactamente donde la persona quiso ir, y pisarla sería peor.
           */
          if (viene === 'login') navigate(guia ? '/bienvenida' : '/', { replace: true });
        })
        .catch((error: unknown) => {
          // Un 401 acá es el estado normal antes del login, no un error que reportar.
          setAuthed(error instanceof ApiRequestError && error.status !== 401 ? true : false);
        });
    },
    [navigate],
  );

  useEffect(() => leerSesion('arranque'), [leerSesion]);

  if (authed === null) return <Loading label={t('app.verificandoSesion')} />;
  if (!authed) return <Login onSuccess={() => leerSesion('login')} />;

  const logout = (): void => {
    void api.post('/api/auth/logout').then(() => {
      // Se limpian junto con la sesión: si no, la próxima persona hereda el menú de la anterior
      // durante el instante que tarda en releerse.
      setEsAdmin(false);
      setLabEnabled(false);
      setNecesitaGuia(false);
      setAuthed(false);
      navigate('/', { replace: true });
    });
  };

  return (
    /*
     * `min-h-dvh` y no `min-h-screen`.
     *
     * `100vh` en un teléfono vale lo que mide la pantalla **con la barra del navegador
     * escondida**, así que mientras está visible la página mide de más y siempre hay unos
     * píxeles de desplazamiento fantasma. `dvh` sigue el alto real mientras cambia.
     */
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      <SideRail onLogout={logout} labEnabled={labEnabled} esAdmin={esAdmin} />
      <TopBar />

      {/*
        El hueco de abajo es de la barra fija: sin él, la última tarjeta de cada pantalla queda
        debajo del menú y no hay forma de llegar a su botón.
      */}
      <main className="mx-auto w-full max-w-6xl px-4 py-6 pb-[calc(var(--barra-inferior)+var(--safe-bottom)+1.5rem)] sm:px-6 lg:px-10 lg:py-10 lg:pb-10">
        {/*
          El límite envuelve las rutas y no la aplicación: así un fallo en una pantalla no se
          lleva el menú, y se puede navegar a otra sección sin escribir la URL a mano.

          La ruta actual lo reinicia. Sin eso, una vez que una sección falla el borde queda en
          error para siempre y las demás pantallas aparecen vacías, porque sigue mostrando el
          fallo anterior.
        */}
        <RoutedContent
          esAdmin={esAdmin}
          labEnabled={labEnabled}
          necesitaGuia={necesitaGuia}
          onGuiaLeida={() => setNecesitaGuia(false)}
        />
      </main>

      <BarraInferior
        labEnabled={labEnabled}
        esAdmin={esAdmin}
        masAbierto={masAbierto}
        onAbrirMas={() => setMasAbierto(true)}
      />
      <HojaDeMas
        abierta={masAbierto}
        onCerrar={() => setMasAbierto(false)}
        onLogout={logout}
        labEnabled={labEnabled}
        esAdmin={esAdmin}
      />
    </div>
  );
}

function RoutedContent({
  esAdmin,
  labEnabled,
  necesitaGuia,
  onGuiaLeida,
}: {
  esAdmin: boolean;
  labEnabled: boolean;
  necesitaGuia: boolean;
  onGuiaLeida: () => void;
}) {
  const { t } = useT();
  const location = useLocation();

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Routes>
        {/*
          Una cuenta nueva ve la guía en vez del tablero.

          Su tablero está vacío —sus influencers son suyas y todavía no tiene ninguna— y un panel
          en cero sin contexto se lee como roto. No es un redirect: el resto del menú sigue
          disponible, y en cuanto marca que la leyó, «Inicio» vuelve a ser el tablero.
        */}
        <Route
          path="/"
          element={necesitaGuia ? <Bienvenida onDone={onGuiaLeida} /> : <Dashboard />}
        />
        <Route path="/nueva" element={<NewCreator />} />
        <Route path="/estadisticas" element={<EstadisticasGenerales />} />
        <Route path="/herramientas" element={<Herramientas />} />
        {/*
          Las dos direcciones que estaban en el menu siguen respondiendo.

          Se movieron adentro de Estadisticas, pero un enlace guardado o un marcador no tienen por
          que romperse: siguen sirviendo la misma pantalla que servian.
        */}
        <Route path="/plataformas" element={<Platforms />} />
        <Route path="/referencias" element={<Referencias />} />
        <Route path="/consumo" element={<Settings />} />
        <Route path="/modelos" element={<Modelos />} />
        <Route path="/paginas" element={<Paginas />} />
        <Route path="/servicios" element={<Services />} />
        {/*
          El laboratorio tampoco existe como ruta si no corresponde.

          Sacarlo del menú no alcanza: escribir la URL a mano lo abría igual. La página fallaba
          en cada llamada —el servidor devuelve 404— pero mostraba su estructura, y una pantalla
          que se abre y no funciona se lee como rota en vez de como prohibida.
        */}
        {labEnabled && <Route path="/laboratorio" element={<Laboratorio />} />}
        <Route path="/credenciales" element={<Credentials />} />
        {/* Se abre en pestaña nueva desde la guía: es referencia, se lee con el panel al lado. */}
        <Route path="/manual" element={<Manual />} />
        <Route path="/bienvenida" element={<Bienvenida onDone={onGuiaLeida} />} />
        {esAdmin && <Route path="/usuarios" element={<Usuarios />} />}
        <Route path="/influencer/:id" element={<CreatorDetail />} />
        <Route path="/influencer/:id/estadisticas" element={<Estadisticas />} />
        <Route path="/workflow/:id" element={<WorkflowRun />} />
        <Route
          path="*"
          element={
            <p className="text-sm text-[var(--color-ink-muted)]">{t('app.paginaInexistente')}</p>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}

/**
 * Marca del producto.
 *
 * El punto que la precede late despacio: es el único movimiento permanente de toda la
 * interfaz, y significa lo mismo que en cualquier panel de control — el sistema está vivo.
 */
function Wordmark() {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden="true"
        className="accent-gradient h-2.5 w-2.5 shrink-0 rounded-full motion-safe:animate-pulse"
      />
      <span className="text-sm font-semibold tracking-tight">
        Virtual Creator <span className="accent-text">OS</span>
      </span>
    </div>
  );
}

/**
 * Navegación lateral, sólo en pantallas anchas.
 *
 * Una barra vertical fija en vez de una horizontal arriba: las etiquetas caben enteras, el
 * elemento activo se lee de un vistazo, y el ancho del contenido deja de competir con el de
 * la navegación. Debajo de `lg` no hay espacio para esto y se usa `TopBar`.
 */
function SideRail({
  onLogout,
  labEnabled,
  esAdmin,
}: {
  onLogout: () => void;
  labEnabled: boolean;
  esAdmin: boolean;
}) {
  const { t } = useT();

  return (
    <aside className="sticky top-0 hidden h-screen flex-col border-r border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)]/50 px-4 py-6 backdrop-blur-xl lg:flex">
      <div className="px-2">
        <Wordmark />
      </div>

      <nav className="mt-8 flex flex-1 flex-col gap-1" aria-label={t('nav.principal')}>
        {navFor(labEnabled, esAdmin).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ' +
              (isActive
                ? 'bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent)]'
                : 'text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]')
            }
          >
            {({ isActive }) => (
              <>
                {/* Marca del elemento activo. Redundante con el color a propósito: el color
                    solo no alcanza para quien no lo distingue. */}
                <span
                  aria-hidden="true"
                  className={
                    'accent-gradient absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full transition-opacity ' +
                    (isActive ? 'opacity-100' : 'opacity-0')
                  }
                />
                <Icon name={item.icon} />
                {t(item.clave)}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] pt-4">
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
        >
          <Icon name="exit" />
          {t('comun.salir')}
        </button>
      </div>
    </aside>
  );
}

/**
 * Cuántos destinos van en la barra inferior antes del botón «Más».
 *
 * Cuatro más «Más» son cinco columnas, que es lo que entra sin apretujarse en el teléfono más
 * angosto que importa (320 px): 64 px por columna, con la zona táctil de 44 px holgada adentro.
 * Con seis, las etiquetas empiezan a cortarse y la fila se vuelve un acertijo de iconos.
 */
const EN_LA_BARRA = 4;

/** Cabecera del teléfono: sólo la marca. Navegar es tarea de la barra de abajo. */
function TopBar() {
  return (
    <header className="barra-fija sticky top-0 z-20 border-b pt-[var(--safe-top)] lg:hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <Wordmark />
      </div>
    </header>
  );
}

/**
 * Navegación del teléfono: una barra fija abajo.
 *
 * ── Por qué abajo y no arriba ──────────────────────────────────────────────────
 * Antes era una tira horizontal debajo de la cabecera, deslizable cuando no entraba. Tenía dos
 * problemas que no se arreglan con estilos. Uno: lo que no entra **no se ve**, y nada avisa de
 * que hay más a la derecha — media navegación quedaba escondida detrás de un gesto que había
 * que adivinar. Dos: estaba arriba, que es donde el pulgar no llega; sostener el teléfono con
 * una mano y estirarse hasta la esquina superior es justo el movimiento que hace que se caiga.
 *
 * Abajo, todo lo que existe se ve, y se ve donde el pulgar ya está.
 *
 * ── Qué entra y qué va en «Más» ────────────────────────────────────────────────
 * Los primeros cuatro del menú, que son los del trabajo diario. El resto —guía, cuentas,
 * laboratorio, ajustes— vive en una hoja que se abre desde el quinto botón. No es esconderlos:
 * es que una barra con nueve iconos de 30 px no es una barra, es una fila de errores de pulsado.
 */
function BarraInferior({
  labEnabled,
  esAdmin,
  onAbrirMas,
  masAbierto,
}: {
  labEnabled: boolean;
  esAdmin: boolean;
  onAbrirMas: () => void;
  masAbierto: boolean;
}) {
  const { t } = useT();
  const items = navFor(labEnabled, esAdmin);

  return (
    <nav
      className="barra-fija fixed inset-x-0 bottom-0 z-30 border-t pb-[var(--safe-bottom)] lg:hidden"
      aria-label={t('nav.principal')}
    >
      <div className="flex items-stretch">
        {items.slice(0, EN_LA_BARRA).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              'tactil flex flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-[0.6875rem] transition ' +
              (isActive
                ? 'font-semibold text-[var(--color-accent)]'
                : 'text-[var(--color-ink-muted)]')
            }
          >
            {({ isActive }) => (
              <>
                {/*
                  La barrita de arriba marca el activo además del color. El color solo no
                  alcanza para quien no lo distingue, y en una barra de iconos chicos es la
                  única otra señal que cabe.
                */}
                <span
                  aria-hidden="true"
                  className={
                    'accent-gradient absolute inset-x-3 top-0 h-0.5 rounded-full transition-opacity ' +
                    (isActive ? 'opacity-100' : 'opacity-0')
                  }
                />
                <Icon name={item.icon} size={20} />
                <span className="max-w-full truncate">{t(item.corta ?? item.clave)}</span>
              </>
            )}
          </NavLink>
        ))}

        <button
          type="button"
          onClick={onAbrirMas}
          aria-expanded={masAbierto}
          className={
            'tactil flex flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-[0.6875rem] transition ' +
            (masAbierto
              ? 'font-semibold text-[var(--color-accent)]'
              : 'text-[var(--color-ink-muted)]')
          }
        >
          <Icon name="menu" size={20} />
          <span>{t('nav.mas')}</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * La hoja de «Más»: el resto del menú y los ajustes de sesión.
 *
 * Sube desde abajo porque es de donde viene el dedo que la abrió, y se cierra tocando fuera,
 * con Escape, o al elegir un destino. Las tres salidas existen porque una hoja de la que no se
 * sabe salir es peor que un menú incompleto.
 */
function HojaDeMas({
  abierta,
  onCerrar,
  onLogout,
  labEnabled,
  esAdmin,
}: {
  abierta: boolean;
  onCerrar: () => void;
  onLogout: () => void;
  labEnabled: boolean;
  esAdmin: boolean;
}) {
  const { t } = useT();

  /*
   * Con la hoja abierta, el fondo no se desplaza.
   *
   * Sin esto, deslizar sobre la hoja arrastra la página de atrás y al cerrarla uno aparece en
   * otro punto del contenido sin haber pedido nada. Se restaura al cerrar y también al
   * desmontar, que es lo que pasa al navegar con la hoja abierta.
   */
  useEffect(() => {
    if (!abierta) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const alEscape = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alEscape);

    return () => {
      document.body.style.overflow = previo;
      document.removeEventListener('keydown', alEscape);
    };
  }, [abierta, onCerrar]);

  if (!abierta) return null;

  const resto = navFor(labEnabled, esAdmin).slice(EN_LA_BARRA);

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <button
        type="button"
        aria-label={t('comun.cerrar')}
        onClick={onCerrar}
        className="absolute inset-0 bg-[oklch(0%_0_0/0.5)] backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.mas')}
        className="barra-fija absolute inset-x-0 bottom-0 rounded-t-3xl border-t px-3 pt-2 pb-[calc(var(--safe-bottom)+1rem)] shadow-[var(--shadow-float)] motion-safe:animate-[subir_0.22s_cubic-bezier(0.32,0.72,0,1)]"
      >
        {/* El tirador no hace nada: dice «esto se agarra desde abajo» sin una sola palabra. */}
        <div
          aria-hidden="true"
          className="mx-auto mb-3 h-1 w-9 rounded-full bg-[var(--color-border-strong)]"
        />

        <div className="grid grid-cols-3 gap-1">
          {resto.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onCerrar}
              className={({ isActive }) =>
                'tactil flex flex-col items-center justify-center gap-1.5 rounded-2xl px-2 py-3 text-center text-xs transition ' +
                (isActive
                  ? 'bg-[var(--color-accent-soft)] font-semibold text-[var(--color-accent)]'
                  : 'text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-sunken)]')
              }
            >
              <Icon name={item.icon} size={20} />
              <span className="leading-tight">{t(item.clave)}</span>
            </NavLink>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] pt-3">
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageToggle />
          </div>
          <button
            type="button"
            onClick={() => {
              onCerrar();
              onLogout();
            }}
            className="tactil flex items-center gap-2 rounded-xl px-3 text-sm text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
          >
            <Icon name="exit" />
            {t('comun.salir')}
          </button>
        </div>
      </div>
    </div>
  );
}
